import React, { useEffect, useRef, useState } from 'react';
import { evaluationsAPI, eventsAPI } from '../api/client';

export function hasOfficialReport(evaluation) {
  // #Fase4-H18 — La API publica disponibilidad, nunca las URLs de las copias.
  return evaluation?.report?.available === true;
}

export default function ReportDownload({ evaluation, role, eventId }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const pending = useRef(false);
  const official = hasOfficialReport(evaluation);
  const published = ['publicada', 'publicada_con_bloqueo'].includes(evaluation?.status);
  const jobStatus = evaluation?.report?.job_status;
  const draft = !eventId && !official && !published && evaluation?.status !== 'archivada' &&
    ['lider_proyecto', 'super_admin'].includes(role);
  const available = Boolean(eventId || official || published || draft);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  async function download() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setMessage('');
    try {
      // #Fase4-F — La sesión determina la variante; nunca se abre directamente report.url.
      const response = eventId
        ? await eventsAPI.getReportPDF(eventId)
        : await evaluationsAPI.getReportPDF(evaluation.id);
      if (response.status === 202) {
        setMessage('El reporte se está generando. Vuelve a intentar en unos momentos.');
        return;
      }
      if (!(response.data instanceof Blob) || !response.data.size ||
        response.data.type.includes('json') || response.data.type.includes('text/')) {
        throw new Error('Respuesta sin PDF');
      }
      const url = URL.createObjectURL(response.data);
      if (draft) {
        setPreview(url);
      } else {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `reporte-${eventId || evaluation.id}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        // #Fase4-F — Se da tiempo al navegador para iniciar la descarga antes de liberar el recurso.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setMessage('Reporte descargado.');
      }
    } catch (error) {
      setMessage(error?.response?.status === 429
        ? 'Hay otro reporte en generación, intenta en un momento'
        : error?.response?.status === 503 ? 'Un administrador debe regenerar el reporte'
        : error?.response?.status === 404 ? 'El reporte no está disponible o no tienes acceso a él.'
        : 'No se pudo obtener el reporte. Vuelve a intentar.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  async function regenerate() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      await evaluationsAPI.regenerateReport(evaluation.id);
      setMessage('Regeneración solicitada. Descarga el reporte en unos momentos.');
    } catch {
      setMessage('No se pudo regenerar el reporte. Vuelve a intentar.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  if (!available) return <p className="text-xs text-slate-500">Reporte disponible al publicar.</p>;
  return <div className="space-y-2" data-report-section>
    {evaluation?.report?.folio && <p className="text-xs font-mono">{evaluation.report.folio}</p>}
    <button type="button" onClick={download} disabled={busy}
      className="soem-btn-secondary px-3 py-2 text-sm disabled:opacity-50"
      data-report-kind={eventId ? 'event' : draft ? 'draft' : 'official'}
      data-testid={eventId ? 'event-report-btn' : `report-${evaluation.id}`}>
      {busy ? 'Procesando...' : eventId ? 'Reporte final del evento' :
        draft ? 'Vista previa — BORRADOR (no oficial)' :
          official ? 'Descargar reporte oficial' : 'Consultar reporte oficial'}
    </button>
    {!eventId && official && role === 'super_admin' && (
      <button type="button" onClick={regenerate} disabled={busy}
        className="soem-btn-secondary px-3 py-2 text-sm disabled:opacity-50">
        Regenerar reporte oficial
      </button>
    )}
    {!eventId && jobStatus && <p className="text-sm">
      {official ? 'Copia vigente disponible. ' : 'Sin copia disponible. '}
      {jobStatus === 'error' ? 'La generación falló; puedes reintentar.' :
        jobStatus === 'listo' ? 'Generación terminada.' : 'Generación pendiente o en curso.'}
    </p>}
    {message && <p role="status" className="text-sm">{message}</p>}
    {preview && <div role="dialog" aria-label="Vista previa — BORRADOR (no oficial)" aria-modal="true"
      className="fixed inset-0 z-50 bg-white p-6 flex flex-col gap-3">
      <div className="flex justify-between items-center">
        <h2 className="font-bold">Vista previa — BORRADOR (no oficial)</h2>
        <button type="button" onClick={() => setPreview(null)} className="soem-btn-secondary px-3 py-2">
          Cerrar vista previa
        </button>
      </div>
      <iframe title="Reporte BORRADOR" src={preview} className="w-full flex-1" />
      <a href={preview} download={`BORRADOR-${evaluation.id}.pdf`} className="underline">
        Descargar BORRADOR
      </a>
    </div>}
  </div>;
}
